/*
 * CAPTURE-OWNED MODULE
 * FeatureRequestDialog — the form behind the header's Feature Request button.
 *
 * ESCAPE IS OURS FIRST. Several screens under this header listen for Escape
 * themselves (the template editor, the zone editor, the processing monitor,
 * the workflow panels), all on window/document in the bubble phase. This
 * dialog listens on window in the CAPTURE phase, which runs before any of
 * them, and stops the event there — so Escape closes the dialog and nothing
 * underneath also reacts. Every other key pressed inside the dialog is stopped
 * the same way: page shortcuts (the review page's arrow keys, Ctrl+S in the
 * template panel) must not fire while someone is typing a request.
 *
 * A FAILED SEND KEEPS WHAT WAS TYPED. The form state lives here and the dialog
 * stays open on failure, with a sentence rather than the server's error code.
 *
 * The page path and build are attached automatically and never shown.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { captureAPI } from '../services/api';
import { BUILD_ID } from '../buildId';
import './FeatureRequestDialog.css';

export const MAX_TEXT = 2000;
export const SUCCESS_CLOSE_MS = 1800;

export const URGENCY_OPTIONS = [
  { value: 'nice_to_have', label: 'Nice to have' },
  { value: 'would_help_a_lot', label: 'Would help a lot' },
  { value: 'blocking_me', label: 'Blocking me' },
];

/** A sentence for the person, never the raw code. */
export function messageForError(err) {
  const status = err?.response?.status;
  const code = err?.response?.data?.error;
  // Its own code only: a 503 is also what capture-service answers when it
  // cannot check the sign-in (auth_unavailable), which is not this.
  if (code === 'feature_request_not_configured') {
    return "Feature requests aren't set up for this workspace yet. Please let your administrator know.";
  }
  if (code === 'rate_limited' || status === 429) {
    return "You've sent quite a few requests in the last hour. Please try again a little later.";
  }
  if (code === 'invalid_request' || status === 400) {
    return 'Something in the form was not accepted. Please check it and try again.';
  }
  if (code === 'feature_request_send_failed' || status === 502) {
    return "We couldn't deliver your request just now. Please try again in a few minutes.";
  }
  return 'Something went wrong sending your request. Please try again.';
}

/**
 * @param {Function} onClose  called with no argument on cancel, Escape, or after success
 */
export default function FeatureRequestDialog({ onClose }) {
  const location = useLocation();
  const [what, setWhat] = useState('');
  const [why, setWhy] = useState('');
  const [urgency, setUrgency] = useState('nice_to_have');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const dialogRef = useRef(null);
  const whatRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const sendingRef = useRef(false);
  sendingRef.current = sending;

  useEffect(() => {
    if (whatRef.current) whatRef.current.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        if (!sendingRef.current) onCloseRef.current();
        return;
      }
      if (dialogRef.current && dialogRef.current.contains(e.target)) {
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  useEffect(() => {
    if (!sent) return undefined;
    const t = setTimeout(() => onCloseRef.current(), SUCCESS_CLOSE_MS);
    return () => clearTimeout(t);
  }, [sent]);

  const canSubmit = !sending && !sent && what.trim().length > 0;

  const submit = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!canSubmit) return;
    setSending(true);
    setError(null);
    try {
      await captureAPI.feedback.submitFeatureRequest({
        what: what.trim(),
        why: why.trim(),
        urgency,
        pagePath: (location.pathname || '').slice(0, 300),
        clientBuild: BUILD_ID ? String(BUILD_ID).slice(0, 64) : null,
      });
      setSent(true);
    } catch (err) {
      setError(messageForError(err));
    } finally {
      setSending(false);
    }
  }, [canSubmit, what, why, urgency, location.pathname]);

  return (
    <div className="frd-overlay" onClick={() => (sending ? null : onClose())}>
      <div
        ref={dialogRef}
        className="frd-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="frd-title"
      >
        <div className="frd-head">
          <h3 className="frd-title" id="frd-title">Request a feature</h3>
          <button type="button" className="frd-close" onClick={onClose} disabled={sending} aria-label="Close">
            &times;
          </button>
        </div>

        {sent ? (
          <p className="frd-success" role="status">Thanks — your request has been sent to the team.</p>
        ) : (
          <form onSubmit={submit}>
            <label className="frd-field">
              <span className="frd-label">What would you like? <span className="frd-required">(required)</span></span>
              <textarea
                ref={whatRef}
                className="frd-textarea"
                rows={4}
                maxLength={MAX_TEXT}
                value={what}
                disabled={sending}
                onChange={(e) => setWhat(e.target.value)}
                required
              />
            </label>

            <label className="frd-field">
              <span className="frd-label">Why does it matter? <span className="frd-optional">(optional)</span></span>
              <textarea
                className="frd-textarea"
                rows={3}
                maxLength={MAX_TEXT}
                value={why}
                disabled={sending}
                onChange={(e) => setWhy(e.target.value)}
              />
            </label>

            <fieldset className="frd-field frd-urgency">
              <legend className="frd-label">Urgency</legend>
              <div className="frd-urgency-options">
                {URGENCY_OPTIONS.map((o) => (
                  <label key={o.value} className={`frd-urgency-option${urgency === o.value ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="frd-urgency"
                      value={o.value}
                      checked={urgency === o.value}
                      disabled={sending}
                      onChange={() => setUrgency(o.value)}
                    />
                    <span>{o.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            {error && <p className="frd-error" role="alert">{error}</p>}

            <div className="frd-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={sending}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
                {sending ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
